'use strict'

import React, {useState, useRef, useEffect, useMemo, useCallback} from 'react';
import {Map as IMap} from 'immutable';
import numeral from "numeral";

import {encodeArray} from "../../shared/pocker-son";
import {formatDate, PrettyDateFormat} from "../../shared/formatters";

function CursorValue({type, value, cc}) {
	return (
		<tspan x="0" dy="1.2em" className={`cursor-value zl-cc-fill-${cc}`}>
			<tspan className="swatch">{'\uf111'}</tspan>
			{' '}
			{type.join('.')}: {numeral(value).format('0,0')}
		</tspan>
	)
}

function CursorValues({left, date, children}) {
	const wrapper = useRef(null);
	const [box, setBox] = useState(null);

	useEffect(() => {
		let w = wrapper.current;
		if (w) {
			let {width, height} = w.getBBox();
			setBox({width: Math.ceil(width), height: Math.ceil(height)});
		}
	}, [date, children]);

	const content = useMemo(() => {
		// Using style for transform so i can use calc
		return (
			<text ref={wrapper} style={{transform: "translate(5px, calc(1em + 5px))"}}>
				<tspan x="0" className='date'>{formatDate(date, PrettyDateFormat)}</tspan>
				{children}
			</text>
		);
	}, [date, children]);

	let transform;
	if (box && left) {
		transform = `translate(${-box.width - 20})`
	} else {
		transform = 'translate(10)';
	}

	return (
		<g className="values" transform={transform}>
			{box && <rect width={box.width + 10} height={box.height + 10} className="background"/>}
			{content}
		</g>
	)
}


function Cursor({width, height, padding, xScale, yScale, data, onCursorChange, onStapleSet}) {
	const trackArea = useRef(null);
	const [state, setState] = useState(new IMap({cursor: null, staple: null}));

	const trackMouse = useCallback((e) => {
		let ta = trackArea.current;
		if (ta) {
			let rect = ta.getBoundingClientRect();
			let pos = e.clientX - rect.left;
			let v = xScale.invert(pos);
			let ts = v.valueOf();
			if (isNaN(ts)) {
				return;
			}

			let h = v.getUTCHours();
			if (h > 12) {
				v.setUTCHours(24, 0, 0, 0);
			} else {
				v.setUTCHours(0, 0, 0, 0);
			}

			setState(state => {
				let c = state.get('cursor');
				if (c === null || c.valueOf() !== v.valueOf()) {
					return state.set('cursor', v);
				} else {
					return state;
				}
			});
		}
	}, [xScale, setState]);

	const setStaple = useCallback((e) => {
		e.preventDefault();
		setState(state => {
			return state.set('staple', state.get('cursor'));
		});
	}, [xScale, setState]);

	useEffect(() => {
		onCursorChange && onCursorChange(state.get('cursor'));
	}, [onCursorChange, state.get('cursor')]);

	useEffect(() => {
		onStapleSet && onStapleSet(state.get('staple'));
	}, [onStapleSet, state.get('staple')]);

	const trackRect = useMemo(() => {
		return <rect
			ref={trackArea}
			width={width - padding.left - padding.right}
			height={height - padding.top - padding.bottom}
			transform={`translate(${padding.left} ${padding.top})`}
			onMouseMove={trackMouse}
			onClick={setStaple}
			className="track-area"
		/>
	}, [width, height, padding]);

	const cursor = useMemo(() => {
		let cursor = state.get('cursor');
		if (!cursor) {
			return null;
		}

		let cc = 0;
		let dots = [];
		let values = [];
		let cursorX = xScale(cursor) + padding.left;

		next_series: for (let {type, series} of data.values()) {
			cc += 1;
			for (let {ts, count} of series) {
				ts = new Date(ts);
				if (ts.valueOf() === cursor.valueOf()) {
					dots.push(
						<circle
							key={encodeArray(type)}
							cy={yScale(count)}
							r={2} className={`zl-cc-fill-${cc}`}/>
					);
					values.push(
						<CursorValue
							key={encodeArray(type)}
							value={count}
							{...{type, cc}}
						/>
					);
					continue next_series;
				}
			}
		}

		return (
			<g transform={`translate(${cursorX},${padding.top})`} className="content">
				<path d={`M0,0V${height - padding.vpadding}`}/>
				{dots}
				<CursorValues date={cursor} left={(cursorX > width / 2)}>
					{values}
				</CursorValues>
			</g>
		);
	}, [height, padding, data, state.get('cursor')]);

	const staple = useMemo(() => {
		let staple = state.get('staple');
		if (!staple) {
			return null;
		}
		let stapleX = xScale(staple) + padding.left;
		return <path d={`M${stapleX},${padding.top}V${height - padding.vpadding}`} className="staple"/>
	}, [height, padding, state.get('staple')]);

	return (
		<g className="cursor">
			{staple}
			{cursor}
			{trackRect}
		</g>
	);
}

export default Cursor;
